import React, { useCallback, useRef, ElementRef, useState } from 'react';
import { classNames, nullFn } from '../../../utils';
import { getInputValue } from '../utils';
import { useDisclosure } from '../../../hooks';
import { TimePickerInputProps } from '../types';
import { TimePickerModal } from './TimePickerModal';
import { TimePicker } from './TimePicker';
import TimerIcon from '../../../../assets/images/svg/TimerIcon';
import CloseButton from '../../button/components/CloseButton';
import { DateTime } from 'luxon';
import { DropDown, ItemDefinitionType } from '../../dorpDown';

type TimePikcerHandle = ElementRef<typeof TimePicker>;

const TimePickerInput = ({
  id = undefined,
  value: valueProp = undefined,
  label,
  name,
  className = undefined,
  modalClassName = undefined,
  style = undefined,
  placeholder = undefined,
  error = undefined,
  readOnly = false,
  onChange = nullFn,
  onClick = nullFn,
  clearable = true,
}: TimePickerInputProps): JSX.Element => {
  const [zone, setTimeZone] = useState<string | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const timePickerRef = useRef<TimePikcerHandle>(null);
  const { isOpen, open: openModal, close: closeModal } = useDisclosure(false);
  const {
    isOpen: tzMenuIsOpen,
    close: closeTXMenu,
    toggle: toggleTZMenu,
  } = useDisclosure(false);

  const timeZones: ItemDefinitionType<string>[] = [
    {
      label: 'Atikokan',
      value: 'America/Atikokan',
    },
    {
      label: 'Blanc-Sablon',
      value: 'America/Blanc-Sablon',
    },
    {
      label: 'Cambdridge Bay',
      value: 'America/Cambdridge_Bay',
    },
    {
      label: 'Coral Harbour',
      value: 'America/Coral_Harbour',
    },
    {
      label: 'Creston',
      value: 'America/Creston',
    },
    {
      label: 'Dawson',
      value: 'America/Dawson',
    },
    {
      label: 'Dawson Creek',
      value: 'America/Dawson_Creek',
    },
    {
      label: 'Edmonton',
      value: 'America/Edmonton',
    },
    {
      label: 'Fort Nelson',
      value: 'America/Fort_Nelson',
    },
    {
      label: 'Halifax',
      value: 'America/Halifax',
    },
    {
      label: 'Inuvik',
      value: 'America/Inuvik',
    },
    {
      label: 'Iqaluit',
      value: 'America/Iqaluit',
    },
    {
      label: 'Moncton',
      value: 'America/Moncton',
    },
    {
      label: 'Montreal',
      value: 'America/Montreal',
    },
    {
      label: 'Nipigon',
      value: 'America/Nipigon',
    },
    {
      label: 'Pangnirtung',
      value: 'America/Pangnirtung',
    },
    {
      label: 'Rainy River',
      value: 'America/Rainy_River',
    },
    {
      label: 'Regina',
      value: 'America/Regina',
    },
    {
      label: 'Resolute',
      value: 'America/Resolute',
    },
    {
      label: 'St. Johns',
      value: 'America/St_Johns',
    },
    {
      label: 'Swift Current',
      value: 'America/Swift_Current',
    },
    {
      label: 'Thunder Bay',
      value: 'America/Thunder_Bay',
    },
    {
      label: 'Toronto',
      value: 'America/Toronto',
    },
    {
      label: 'Vancouver',
      value: 'America/Vancouver',
    },
    {
      label: 'Whitehorse',
      value: 'America/Whitehorse',
    },
    {
      label: 'Winnipeg',
      value: 'America/Winnipeg',
    },
    {
      label: 'Yellowknife',
      value: 'America/Yellowknife',
    },
  ];

  const handleOnChangeTimeZone = (value?: ItemDefinitionType<string>) => {
    if (!value) return;

    if (value.value === '') {
      setTimeZone(undefined);
    } else {
      setTimeZone(value.value);
    }

    closeTXMenu();
  };

  const getTriggeredInputValue = () => {
    return getInputValue(
      valueProp ? DateTime.fromJSDate(valueProp, { zone: zone }) : undefined,
    );
  };

  const handleOnChange = useCallback(
    (newValue?: DateTime) => {
      const jsDate = newValue ? newValue.toJSDate() : undefined;
      closeModal();
      onChange(jsDate);
    },
    [closeModal, onChange],
  );

  const handleClick = useCallback(
    (
      event: React.MouseEvent<
        HTMLElement,
        React.MouseEvent<Element, MouseEvent>
      >,
    ) => {
      if (!readOnly) {
        openModal();
        onClick(event);
      }
    },
    [onClick, openModal, readOnly],
  );

  const setFocusToTimeInput = () => {
    if (timePickerRef.current) {
      timePickerRef.current.setFocusToHourInput();
    }
  };

  const handleClearTimeZone = useCallback(() => {
    closeTXMenu();
    setTimeZone(undefined);
  }, [closeTXMenu]);

  const handleClearValue = useCallback(() => {
    closeModal();
    handleClearTimeZone();
    onChange(undefined);
  }, [closeModal, handleClearTimeZone, onChange]);

  // useEffect(() => {
  //   setValue(get12HourTime(valueProp));
  // }, [valueProp]);

  return (
    <div
      className={classNames('flex w-auto flex-col', className ? className : '')}
      id={id}
      style={style}
    >
      <div className='mb-2 flex flex-row items-center justify-between'>
        <label
          htmlFor={name}
          className='text-base font-medium tracking-tight text-gray-dark'
        >
          {label}
        </label>
        {zone ? (
          <div className='flex flex-row items-center space-x-2'>
            <CloseButton size={6} onClick={handleClearTimeZone} />
            <span className='text-xs font-light text-gray'>{zone}</span>
          </div>
        ) : null}
      </div>
      <div className='relative w-full'>
        <input
          id={name}
          name={name}
          value={getTriggeredInputValue()}
          type='text'
          autoComplete='off'
          readOnly
          onClick={handleClick}
          placeholder={placeholder}
          ref={inputRef}
          className='w-full appearance-none rounded-lg border border-gray-light px-16 py-4 pr-5 text-base font-medium placeholder-gray-light shadow-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-green'
        />
        <button type='button' onClick={toggleTZMenu}>
          <TimerIcon
            width='24px'
            height='24px'
            className='absolute left-0 top-1/2 ml-5 -translate-y-1/2 transform stroke-current text-green'
          />
        </button>

        {valueProp !== undefined && clearable ? (
          <CloseButton
            onClick={handleClearValue}
            className='absolute right-0 top-1/2 mr-5 -translate-y-1/2 transform'
          />
        ) : null}

        {tzMenuIsOpen ? (
          <div className='absolute mt-1'>
            <DropDown
              items={timeZones}
              onChanged={handleOnChangeTimeZone}
              selected={timeZones.find((item) => item.value === zone)}
            />
          </div>
        ) : null}
      </div>

      {error ? error : null}

      <TimePickerModal
        id={id}
        isOpen={isOpen}
        onClose={closeModal}
        onOpened={setFocusToTimeInput}
        className={modalClassName}
      >
        <TimePicker
          onCloseModal={closeModal}
          ref={timePickerRef}
          onChange={handleOnChange}
          value={
            valueProp
              ? DateTime.fromJSDate(valueProp, { zone: zone })
              : undefined
          }
          zone={zone}
        />
      </TimePickerModal>
    </div>
  );
};

export default TimePickerInput;
